import React from "react";
import { Alert, Box, Button, Paper, Stack, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { spacing } from "../../styles/theme";
import { DisplaySelect } from "./SpecsLayerSpecsControls";
import { SpecsLayerSpecsLayerFields } from "./SpecsLayerSpecsLayerFields";
import { SpecsLayerSpecsStyleFields } from "./SpecsLayerSpecsStyleFields";
import { serializeSpecsNavHash } from "./specsNavHash";
import { useSpecsEditorContextValue } from "./useSpecsEditor";
import type { ElementModel } from "./specsModel/ElementModel";
import type { LayerModel } from "./specsModel/LayerModel";
import type { PartModel } from "./specsModel/PartModel";
import type { StyleModel } from "./specsModel/StyleModel";
import type { VersionModel } from "./specsModel/VersionModel";

type LayerSpecsSelection = {
  part: PartModel | null;
  element: ElementModel | null;
  style: StyleModel | null;
  layer: LayerModel | null;
};

function resolveSelection({
  version,
  partName,
  elementName,
  styleName,
  layerName,
}: {
  version: VersionModel;
  partName?: string;
  elementName?: string;
  styleName?: string | null;
  layerName?: string;
}): LayerSpecsSelection {
  let part = partName ? version.getPart(partName) : null;
  if (!part) {
    const firstPart = version.parts[0];
    part = firstPart ? firstPart : null;
  }

  const matchingElement =
    part && elementName
      ? part.elements.find((candidate) => candidate.name === elementName)
      : null;
  let element = matchingElement ? matchingElement : null;
  if (!element && part) {
    const firstElement = part.elements[0];
    element = firstElement ? firstElement : null;
  }

  const matchingStyle =
    element && styleName
      ? element.styles.find((candidate) => candidate.name === styleName)
      : null;
  let style = matchingStyle ? matchingStyle : null;
  if (!style && element) {
    const firstStyle = element.styles[0];
    style = firstStyle ? firstStyle : null;
  }

  const matchingLayer =
    style && layerName
      ? style.layers.find((candidate) => candidate.name === layerName)
      : null;
  let layer = matchingLayer ? matchingLayer : null;
  if (!layer && style) {
    const defaultLayer = style.getDefaultLayer();
    layer = defaultLayer ? defaultLayer : null;
  }

  return { part, element, style, layer };
}

export default function SpecsLayerSpecsPage() {
  const { currentVersion, nav, editor, selectedElement, selectedStyleName } =
    useSpecsEditorContextValue();

  if (!currentVersion) {
    return (
      <Alert severity="info">
        Import or create a product before editing layer specs.
      </Alert>
    );
  }

  const partName =
    nav.kind === "layerSpecs" ? nav.part : selectedElement?.part.name;
  const elementName =
    nav.kind === "layerSpecs" ? nav.element : selectedElement?.element.name;
  const styleName = nav.kind === "layerSpecs" ? nav.style : selectedStyleName;
  const layerName = nav.kind === "layerSpecs" ? nav.layerName : undefined;
  const { part, element, style, layer } = resolveSelection({
    version: currentVersion,
    partName,
    elementName,
    styleName,
    layerName,
  });
  const layerModelPath =
    part && element && style && layer
      ? {
          partName: part.name,
          elementName: element.name,
          styleName: style.name,
          layerName: layer.name,
        }
      : null;
  const styleDebugPath = style?._debugPath;
  const layerDebugPath = layer?._debugPath;
  const designLayer = editor.allLayers.find((candidate) => {
    return (
      candidate.modelLayerPath?.partName === part?.name &&
      candidate.modelLayerPath?.elementName === element?.name &&
      candidate.modelLayerPath?.styleName === style?.name &&
      candidate.modelLayerPath?.layerName === layer?.name
    );
  });
  const designLayerNav =
    part && element && style && designLayer
      ? ({
          kind: "layer",
          product: editor.identity.productName,
          version: editor.identity.productVersion,
          part: part.name,
          element: element.name,
          style: style.name,
          layerId: designLayer.id,
        } as const)
      : null;

  const updateNav = (next: {
    part?: string;
    element?: string;
    style?: string;
    layerName?: string;
  }) => {
    let nextPart = next.part;
    if (!nextPart) {
      if (!part) {
        return;
      }
      nextPart = part.name;
    }

    let nextElement = next.element;
    if (!nextElement) {
      if (!element) {
        return;
      }
      nextElement = element.name;
    }

    let nextStyle = next.style;
    if (!nextStyle) {
      if (!style) {
        return;
      }
      nextStyle = style.name;
    }

    let nextLayer = next.layerName;
    if (!nextLayer) {
      if (!layer) {
        return;
      }
      nextLayer = layer.name;
    }

    editor.setNav({
      kind: "layerSpecs",
      product: editor.identity.productName,
      version: editor.identity.productVersion,
      part: nextPart,
      element: nextElement,
      style: nextStyle,
      layerName: nextLayer,
    });
  };
  return (
    <Paper variant="outlined" sx={{ p: spacing.groupingDesktop }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Typography variant="h2">Layer specs</Typography>
        {designLayerNav ? (
          <Button
            href={serializeSpecsNavHash(designLayerNav)}
            size="small"
            variant="outlined"
            startIcon={<OpenInNewIcon fontSize="small" />}
            onClick={(event) => {
              if (event.metaKey || event.ctrlKey || event.shiftKey) {
                return;
              }

              event.preventDefault();
              editor.setNav(designLayerNav);
            }}
          >
            Open design layer
          </Button>
        ) : null}
      </Stack>

      <Stack spacing={3}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" },
            gap: 2,
          }}
        >
          <DisplaySelect
            label="Part"
            debugPath={part?._debugPath ? `${part._debugPath}.name` : undefined}
            value={part ? part.name : ""}
            options={currentVersion.parts.map((item) => item.name)}
            onChange={(value) => {
              const nextPart = currentVersion.getPart(value);
              if (!nextPart) {
                return;
              }

              const nextElement = nextPart.elements[0];
              if (!nextElement) {
                return;
              }

              const nextStyle = nextElement.styles[0];
              if (!nextStyle) {
                return;
              }

              const nextLayer = nextStyle.getDefaultLayer();
              if (!nextLayer) {
                return;
              }

              updateNav({
                part: value,
                element: nextElement.name,
                style: nextStyle.name,
                layerName: nextLayer.name,
              });
            }}
          />
          <DisplaySelect
            label="Element"
            debugPath={
              element?._debugPath ? `${element._debugPath}.name` : undefined
            }
            value={element ? element.name : ""}
            options={part ? part.elements.map((item) => item.name) : []}
            onChange={(value) => {
              if (!part) {
                return;
              }

              const nextElement = part.elements.find(
                (candidate) => candidate.name === value
              );
              if (!nextElement) {
                return;
              }

              const nextStyle = nextElement.styles[0];
              if (!nextStyle) {
                return;
              }

              const nextLayer = nextStyle.getDefaultLayer();
              if (!nextLayer) {
                return;
              }

              updateNav({
                element: value,
                style: nextStyle.name,
                layerName: nextLayer.name,
              });
            }}
          />
          <DisplaySelect
            label="Style"
            debugPath={styleDebugPath ? `${styleDebugPath}.name` : undefined}
            value={style ? style.name : ""}
            options={element ? element.styles.map((item) => item.name) : []}
            onChange={(value) => {
              if (!element) {
                return;
              }

              const nextStyle = element.styles.find(
                (candidate) => candidate.name === value
              );
              if (!nextStyle) {
                return;
              }

              const nextLayer = nextStyle.getDefaultLayer();
              if (!nextLayer) {
                return;
              }

              updateNav({
                style: value,
                layerName: nextLayer.name,
              });
            }}
          />
          <DisplaySelect
            label="Layer"
            debugPath={layerDebugPath ? `${layerDebugPath}.name` : undefined}
            value={layer ? layer.name : ""}
            options={style ? style.layers.map((item) => item.name) : []}
            onChange={(value) => updateNav({ layerName: value })}
          />
        </Box>

        {!element || !style || !layer ? (
          <Alert severity="info">
            Select a style and layer to edit specs fields.
          </Alert>
        ) : (
          <>
            <SpecsLayerSpecsLayerFields
              layer={layer}
              debugPath={layerDebugPath ?? undefined}
            />
            <SpecsLayerSpecsStyleFields
              style={style}
              styleOptions={element.styles.map((item) => item.name)}
              debugPath={styleDebugPath ?? undefined}
            />
          </>
        )}
      </Stack>
    </Paper>
  );
}
